import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, ElementRef, NgZone, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { Toast, ToastPackage, ToastrService } from 'ngx-toastr';

@Component({
  selector: '[toast-component]', // eslint-disable-line
  templateUrl: './copy-toast.component.html',
  styleUrls: ['./copy-toast.component.scss'],
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  animations: [
    trigger('flyInOut', [
      state('inactive', style({ opacity: 0 })),
      state('active', style({ opacity: 1 })),
      state('removed', style({ opacity: 0 })),
      transition('inactive => active', animate('{{ easeTime }}ms {{ easing }}')),
      transition('active => removed', animate('{{ easeTime }}ms {{ easing }}')),
    ]),
  ],
})
export class CopyToastComponent extends Toast implements OnDestroy {
  copied = false;
  private copiedResetHandle: ReturnType<typeof setTimeout>;

  @ViewChild('toastMessageEl') toastMessageEl?: ElementRef<HTMLElement>;

  constructor(
    protected toastrService: ToastrService,
    public toastPackage: ToastPackage,
    protected ngZone?: NgZone
  ) {
    super(toastrService, toastPackage, ngZone);
  }

  override ngOnDestroy(): void {
    if (this.copiedResetHandle) {
      clearTimeout(this.copiedResetHandle);
    }
    super.ngOnDestroy();
  }

  /** ngx-toastr passes icon class names (e.g. `toast-error`), not literal `error`. */
  get showCopyButton(): boolean {
    const t = (this.toastPackage?.toastType || '').toLowerCase();
    return t.includes('error') || t.includes('warning');
  }

  copyMessage(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    const text = this.getPlainTextToCopy();
    if (!text) {
      return;
    }
    this.writeClipboard(text).then((ok) => {
      if (ok) {
        this.copied = true;
        if (this.copiedResetHandle) {
          clearTimeout(this.copiedResetHandle);
        }
        this.copiedResetHandle = setTimeout(() => {
          this.copied = false;
        }, 2000);
      }
    });
  }

  private getPlainTextToCopy(): string {
    const titlePart = this.title ? String(this.title).trim() : '';

    // Prefer the original package payload — binding multi-line text as
    // aria-label truncates at the first newline in some browsers.
    const rawCandidates = [
      this.toastPackage?.message,
      this.message,
      this.toastMessageEl?.nativeElement?.innerText,
      this.toastMessageEl?.nativeElement?.textContent,
    ];

    let body = '';
    for (const raw of rawCandidates) {
      if (raw == null) continue;
      let text = typeof raw === 'string' ? raw : String(raw);
      if (this.options.enableHtml && text.includes('<')) {
        text = this.stripHtml(text);
      }
      text = text.replace(/\r\n/g, '\n').trim();
      if (text.length > body.length) {
        body = text;
      }
    }

    if (titlePart && body) {
      return `${titlePart}\n\n${body}`;
    }
    return titlePart || body;
  }

  private stripHtml(html: string): string {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  private async writeClipboard(text: string): Promise<boolean> {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      /* fall through */
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      ta.style.whiteSpace = 'pre';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, text.length);
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}
